import { map } from 'lodash';

import {
  NODE_TYPES,
  ANSWER_TYPES,
  SHUFFLE_TYPES,
} from '@pollfish/pf-library-of-power';

export const {
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_DESCRIPTION,
  QUESTION_RATING,
  QUESTION_SLIDER,
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERIC_OPEN_ENDED: QUESTION_NUMERICAL_OPEN_ENDED,
  QUESTION_RANKING,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_NPS,
  QUESTION_VAN_WESTENDORP: QUESTION_MATRIX_VAN_WESTENDORP,
  QUESTION_MAXDIFF: QUESTION_MATRIX_MAXDIFF,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_DRILLDOWN,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_NUMERIC_OPEN_ENDED: QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
  QUESTION_MATRIX_CONSTANT_SUM: QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
  QUESTION_CONVERSATIONAL,
  GROUP_BASIC: GROUP_TYPE_BASIC,
  GROUP_AB_TEST: GROUP_TYPE_AB,
  GROUP_CONJOINT: GROUP_TYPE_CONJOINT,
  GROUP_VAN_WESTENDORP: GROUP_TYPE_VAN_WESTENDORP,
  GROUP_MAXDIFF: GROUP_TYPE_MAXDIFF,
  GROUP_LOOP_AND_MERGE: GROUP_TYPE_LOOP_AND_MERGE,
} = NODE_TYPES;

export const {
  ANSWER_DEFAULT,
  ANSWER_OTHER,
  ANSWER_NONE_OF_THE_ABOVE,
} = ANSWER_TYPES;

export const {
  SHUFFLE_NONE,
  SHUFFLE_WITH_LAST_FIXED,
  SHUFFLE_ALL,
  SHUFFLE_REVERSE,
  SHUFFLE_REVERSE_WITH_LAST_FIXED,
  SHUFFLE_REVERSE_WITH_LAST_TWO_FIXED,
  SHUFFLE_WITH_LAST_TWO_FIXED,
  SHUFFLE_WITH_FIRST_FIXED,
  SHUFFLE_WITH_FIRST_TWO_FIXED,
  SHUFFLE_WITH_FIRST_AND_LAST_FIXED,
  SHUFFLE_ROWS,
  SHUFFLE_ROWS_REVERSE,
  SHUFFLE_ROWS_REVERSE_WITH_LAST_FIXED,
  SHUFFLE_ROWS_REVERSE_WITH_LAST_TWO_FIXED,
  SHUFFLE_ROWS_WITH_LAST_FIXED,
  SHUFFLE_ROWS_WITH_LAST_TWO_FIXED,
  SHUFFLE_ROWS_WITH_FIRST_FIXED,
  SHUFFLE_ROWS_WITH_FIRST_TWO_FIXED,
  SHUFFLE_ROWS_WITH_FIRST_AND_LAST_FIXED,
  SHUFFLE_ROWS_REVERSE_WITH_FIRST_FIXED,
  SHUFFLE_ROWS_REVERSE_WITH_FIRST_TWO_FIXED,
  SHUFFLE_COLUMNS,
  SHUFFLE_COLUMNS_REVERSE,
  SHUFFLE_COLUMNS_REVERSE_WITH_LAST_FIXED,
  SHUFFLE_COLUMNS_REVERSE_WITH_LAST_TWO_FIXED,
  SHUFFLE_COLUMNS_WITH_LAST_FIXED,
  SHUFFLE_COLUMNS_WITH_LAST_TWO_FIXED,
  SHUFFLE_COLUMNS_WITH_FIRST_FIXED,
  SHUFFLE_COLUMNS_WITH_FIRST_TWO_FIXED,
  SHUFFLE_COLUMNS_WITH_FIRST_AND_LAST_FIXED,
  SHUFFLE_ROWS_AND_SHUFFLE_COLUMNS_REVERSE,
} = SHUFFLE_TYPES;

export const INTERNAL_SURVEY_TYPE = 'PublisherOwn';
export const SURVEY_TYPE_SIMILAR_ON_EXPIRE = 'similar';
export const SURVEY_TYPE_ADDITIONAL_ON_EXPIRE = 'additional';

// It will be used to send the request post data as url encoded
export const TRANSFORMATION_DATA_TYPE_URL_PARAMS = 'urlParams';
// It will be used to send the request post data as json
export const TRANSFORMATION_DATA_TYPE_JSON = 'json';

export const SUPPORTED_LANGUAGES = [
  {
    key: 'en',
    value: 'English',
  },
  {
    key: 'el',
    value: 'Ελληνικά',
  },
  {
    key: 'ru',
    value: 'русский',
  },
  {
    key: 'es',
    value: 'Español',
  },
  {
    key: 'de',
    value: 'Deutsch',
  },
  {
    key: 'fr',
    value: 'Français',
  },
  {
    key: 'it',
    value: 'Italiano',
  },
  {
    key: 'ar',
    value: 'العربية',
  },
  {
    key: 'hi',
    value: 'हिंदी',
  },
  {
    key: 'tr',
    value: 'Türkçe',
  },
  {
    key: 'pt',
    value: 'Português',
  },
  {
    key: 'ja',
    value: '日本語',
  },
  {
    key: 'zh',
    value: '中文',
  },
  {
    key: 'da',
    value: 'Dansk',
  },
  {
    key: 'sv',
    value: 'Svenska',
  },
  {
    key: 'bg',
    value: 'български',
  },
  {
    key: 'ro',
    value: 'Română',
  },
  {
    key: 'id',
    value: 'Indonesian',
  },
  {
    key: 'ph',
    value: 'Filipino',
  },
  {
    key: 'cs',
    value: 'český',
  },
  {
    key: 'nl',
    value: 'Nederlands',
  },
  {
    key: 'sr',
    value: 'Српски',
  },
  {
    key: 'fi',
    value: 'Suomi',
  },
  {
    key: 'vi',
    value: 'Tiếng Việt',
  },
  {
    key: 'th',
    value: 'ไทย',
  },
  {
    key: 'pl',
    value: 'Polski',
  },
  {
    key: 'ko',
    value: '한국어', // korean
  },
  {
    key: 'hu',
    value: 'Magyar', // hungarian
  },
  {
    key: 'zh-Hant',
    value: '繁體中文', // traditional chinese
  },
  {
    key: 'no',
    value: 'Norsk', // norwegian
  },
  {
    key: 'eg',
    value: 'المصرية', // Egyptian
  },
  {
    key: 'uk',
    value: 'Українська', // ukranian
  },
  {
    key: 'he',
    value: 'עברית', // Hebrew
  },
  {
    key: 'bn',
    value: 'বাঙালী', // Bengali
  },
  {
    key: 'sk',
    value: 'Slovenčina', // slovak
  },
  {
    key: 'fa',
    value: 'فارسی', // persian
  },
  {
    key: 'az',
    value: 'Azərbaycanca', // azerbaijani
  },
  {
    key: 'ka',
    value: 'ქართული', // georgian
  },
  {
    key: 'lt',
    value: 'Lietuvių', // lithuanian
  },
  {
    key: 'pa',
    value: 'ਪੰਜਾਬੀ', // punjabi
  },
  {
    key: 'ps',
    value: 'پښتو', // pashto
  },
  {
    key: 'et',
    value: 'Eesti', // Estonian
  },
  {
    key: 'uz',
    value: 'Uzbekų', // Uzbek
  },
  {
    key: 'lv',
    value: 'Latviešu valoda',
  },
  {
    key: 'ms',
    value: 'Bahasa Melayu',
  },
  {
    key: 'ur',
    value: 'اردو',
  },
  {
    key: 'ur-roman',
    value: 'Urdu Roman',
  },
  {
    key: 'sq',
    value: 'Shqip',
  },
];

export const BLANK_PIPING_ANSWER = '______';

export const GENERIC_DISQUALIFICATION = 'generic_disqualification';
export const SANITY_DISQUALIFICATION = 'sanity_disqualification';
export const DISQUALIFICATION_USER_UNDERAGE = 'user_underage';
export const QUALITY_DISQUALIFICATION = 'quality_disqualification';

export const MAX_PARTICIPATION_WAIT_SUCCESS_MILLISECONDS = 15000;
export const MAX_PARTICIPATION_WAIT_SCREENOUT_MILLISECONDS = 6000;

export const PARTICIPATION_RESULT_DISPLAY_MILLISECONDS = 10000;

export const PARTICIPATION_STATUS = {
  // Redirect to Q1 screen  - Thank you screen
  SUCCESS: 'success',
  // Redirect to Q2 screen  - Screen-out screen
  SCREEN_OUT: 'screen_out',
  // Redirect to Q3 screen  - Survey Expired screen
  SURVEY_EXPIRED: 'survey_expired',
  // Redirect to Q9 screen  - Survey Replaced in Background screen
  SURVEY_REPLACED: 'survey_replaced',
  // Redirect to Q3 screen  - Underage screen-out screen
  DISQUALIFICATION_USER_UNDERAGE: 'underage_screen_out',
  // Redirect to Q11 screen - VPN screen-out screen
  VPN: 'vpn',
  // final quality screenout means that we won't be trying
  // to provide respondent with another survey after this
  // Redirect to Q12 screen - Quality screen-out screen
  FINAL_QUALITY_SCREEN_OUT: 'final_quality_screen_out',
  // Redirect to Q4 screen  - Quality mid screen-out screen
  TENTATIVE_QUALITY_SCREEN_OUT: 'tentantive_quality_screen_out',
  // Redirect to Q3 screen  - User Rejection screen
  USER_REJECTION: 'user_rejection',
  // Respondent has already answered once the survey
  SURVEY_ALREADY_TAKEN: 'survey_already_taken',
  // Survey is not active
  SURVEY_NOT_ACTIVE: 'survey_not_active',
};

export const EXPECTED_PAYLOAD_STATUS_ENUMS = [
  'pending',
  'processing',
  'success',
  'screened_out',
];

export const EXPECTED_PARTICIPATION_STATUS_ENUMS = map(
  PARTICIPATION_STATUS,
  (status) => status,
);
export const PENDING_PARTICIPATION_STATUSES = ['pending', 'processing'];

export const MEDIA_TYPE_IMAGE = 'image';
export const MEDIA_TYPE_AUDIO = 'audio';
export const MEDIA_TYPE_VIDEO = 'video';

export const MEDIA_TYPES = [
  MEDIA_TYPE_IMAGE,
  MEDIA_TYPE_AUDIO,
  MEDIA_TYPE_VIDEO,
];
export const INTERACTIVE_MEDIA_TYPES = [MEDIA_TYPE_AUDIO, MEDIA_TYPE_VIDEO];

export const IGNORED_UNHANDLED_PROMISE_REJECTION_CORS =
  'Blocked a frame with origin "https://wss.pollfish.com" from accessing a cross-origin frame.';
export const IGNORED_UNHANDLED_PROMISE_REJECTION_REASONS = [
  'Ethereum is not initialized',
  '$ is not defined',
  'goog is not defined',
  'chain is not set up',
  "Cannot read properties of null (reading 'tronlinkParams')",
  'Cannot redefine property: visibilityState',
  'Cannot redefine property: ethereum',
];
export const IGNORED_UNHANDLED_PROMISE_REJECTION_CORS_WEBSITES = [
  'https://earnbitmoon.club/',
  'https://claimbits.net/',
  'https://coinadster.com/',
  'https://esfaucets.com/',
];
export const IGNORED_UNHANDLED_PROMISE_REJECTION_BY_EXTENSION = [
  'chrome-extension://dhdgffkkebhmkfjojejmpbldmpobfkfo',
  'chrome-extension://eeagobfjdenkkddmbclomhiblgggliao',
  'chrome-extension://lgmpcpglpngdoalbgeoldeajfclnhafa',
  'chrome-extension://ebfidpplhabeedpnhjnobghokpiioolj',
  'moz-extension://8f96935c-b8a7-457a-8faa-c432eb9fff91',
];

export const DISTRIBUTION_LINK_ENDPAGE_BUTTON_TYPES = {
  // Done button
  DEFAULT: 0,
  // Results button
  RESULTS: 1,
  // Immediately redirect to custom end page
  CUSTOM: 2,
  // Without a button
  NO_REDIRECT: 3,
  // Immediately redirect to a prodege custom end page
  PRODEGE_CUSTOM: 4,
};

export const DISTRIBUTION_LINK_ENDPAGE_TYPES = {
  // Screen out with survey already taken
  SURVEY_ALREADY_TAKEN: 1,
  // Screen out with survey not active
  SURVEY_NOT_ACTIVE: 2,
  // Screen out with survey expired
  SURVEY_EXPIRED: 3,
};

export const RANKING_QUESTION_LABELS_SORT_ASCENDING = 'ASC';
export const RANKING_QUESTION_LABELS_SORT_DESCENDING = 'DESC';

// the value which the mobile will send when make the survey completed request
// based on the current survey state, keep in mind the backend expect
// the following specified literals
export const SURVEY_STATE = {
  STARTING_SURVEY: 'starting',
  ANSWERING_SCREENING: 'screening',
  ANSWERING_SURVEY_QUESTION: 'surveyQuestion',
  COMPLETING_SURVEY: 'complete',
};

export const MAX_TEXT_LENGTH = 500;
export const MIN_TEXT_LENGTH = 2;

export const SIMPLE_MATRIX_APPEARANCE_STYLES = {
  VERTICAL: 'VERTICAL',
  HORIZONTAL: 'HORIZONTAL',
};

export const PILCROW_CHARACTER = '¶';
export const PILCROW_REGEX = /¶/g;

export const AI_CONVERSATIONAL_ANSWER_TYPES = {
  OBJECTIVE: 1,
  GOAL: 2,
};

export const CONVERSATIONAL_AI_VALUE_STATUSES = {
  ONGOING_CONVERSATION: 'ongoing_conversation',
  SUCCESS: 'successful',
  FAILURE: 'unsuccessful',
};

export const CONVERSATIONAL_AI_STATUSES = {
  ERROR: 'error',
  RETRY: 'retry',
};

export const CONVERSATIONAL_AI_MESSAGE_TYPES = {
  AI: 'ai',
  USER: 'user',
};

export const NODE_TYPES_SUPPORT_INPUT_SIZES = [
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_OPEN_ENDED_NUMERIC,
  QUESTION_MATRIX_OPEN_ENDED_CONSTANT_SUM,
];
